import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [tab, setTab] = useState('general');
  const [settings, setSettings] = useState({});
  const [stages, setStages] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [wonReasons, setWonReasons] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(null); // 'stage' | 'activity' | 'won' | 'lost' | 'field'
  const [addForm, setAddForm] = useState({});

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    loadAll();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = () => {
    const h = getAuthHeaders();
    fetch(`${API}/companies/${companyId}/leads/settings`, { headers: h }).then(r => r.ok ? r.json() : {}).then(d => setSettings(d || {})).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/settings/pipeline-stages`, { headers: h }).then(r => r.ok ? r.json() : []).then(d => setStages(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/settings/activity-types`, { headers: h }).then(r => r.ok ? r.json() : []).then(d => setActivityTypes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/settings/won-reasons`, { headers: h }).then(r => r.ok ? r.json() : []).then(d => setWonReasons(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/settings/lost-reasons`, { headers: h }).then(r => r.ok ? r.json() : []).then(d => setLostReasons(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/settings/custom-fields`, { headers: h }).then(r => r.ok ? r.json() : []).then(d => setCustomFields(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const saveSettings = () => {
    setSaving(true);
    fetch(`${API}/companies/${companyId}/leads/settings`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) setSettings(d); })
      .finally(() => setSaving(false));
  };

  const seedDefaults = () => {
    if (!window.confirm('Seed default pipeline stages, activity types, won/lost reasons, and sources?')) return;
    fetch(`${API}/companies/${companyId}/leads/settings/seed-defaults`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) { window.alert('Defaults seeded successfully!'); loadAll(); } });
  };

  const addItem = () => {
    let url, data;
    switch (showAddModal) {
      case 'stage': url = `${API}/companies/${companyId}/leads/settings/pipeline-stages`; data = addForm; break;
      case 'activity': url = `${API}/companies/${companyId}/leads/settings/activity-types`; data = addForm; break;
      case 'won': url = `${API}/companies/${companyId}/leads/settings/won-reasons`; data = addForm; break;
      case 'lost': url = `${API}/companies/${companyId}/leads/settings/lost-reasons`; data = addForm; break;
      case 'field': url = `${API}/companies/${companyId}/leads/settings/custom-fields`; data = addForm; break;
      default: return;
    }
    fetch(url, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowAddModal(null); setAddForm({}); loadAll(); })
      .catch(() => window.alert('Failed to add item'));
  };

  const deleteItem = (type, id) => {
    if (!window.confirm('Delete this item?')) return;
    let url;
    switch (type) {
      case 'stage': url = `${API}/companies/${companyId}/leads/settings/pipeline-stages/${id}`; break;
      case 'activity': url = `${API}/companies/${companyId}/leads/settings/activity-types/${id}`; break;
      case 'won': url = `${API}/companies/${companyId}/leads/settings/won-reasons/${id}`; break;
      case 'lost': url = `${API}/companies/${companyId}/leads/settings/lost-reasons/${id}`; break;
      case 'field': url = `${API}/companies/${companyId}/leads/settings/custom-fields/${id}`; break;
      default: return;
    }
    fetch(url, { method: 'DELETE', headers: getAuthHeaders() })
      .then(r => { if (r.ok) loadAll(); else window.alert('Failed to delete'); });
  };

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const tabStyle = (t) => ({ padding: '10px 20px', border: 'none', borderBottom: tab === t ? '3px solid #6366f1' : '3px solid transparent', background: 'none', fontWeight: tab === t ? 700 : 400, color: tab === t ? '#6366f1' : '#6b7280', cursor: 'pointer', fontSize: 14 });

  const renderList = (items, type, labelField = 'name') => (
    <div>
      {items.map(item => (
        <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
          <div>
            <span style={{ fontWeight: 600 }}>{item[labelField]}</span>
            {item.color && <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', background: item.color, marginLeft: 8, verticalAlign: 'middle' }} />}
            {item.probability !== undefined && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>{item.probability}%</span>}
            {item.icon && <span style={{ marginLeft: 8 }}>{item.icon}</span>}
            {item.data_type && <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>({item.data_type})</span>}
            {item.description && <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.description}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {item.sort_order !== undefined && <span style={{ fontSize: 11, color: '#9ca3af' }}>#{item.sort_order}</span>}
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: item.is_active !== false ? '#f0fdf4' : '#f3f4f6', color: item.is_active !== false ? '#16a34a' : '#9ca3af' }}>
              {item.is_active !== false ? 'Active' : 'Inactive'}
            </span>
            <button onClick={() => deleteItem(type, item.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #ef4444', color: '#ef4444', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
          </div>
        </div>
      ))}
      {items.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No items. Click "Add" or "Seed Defaults" to get started.</div>}
    </div>
  );

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Leads Settings</h2>
        <button onClick={seedDefaults} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>Seed Defaults</button>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 24, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {[['general', 'General'], ['stages', 'Pipeline Stages'], ['activities', 'Activity Types'], ['wonlost', 'Won/Lost Reasons'], ['fields', 'Custom Fields']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={tabStyle(key)}>{label}</button>
        ))}
      </div>

      {/* General Settings */}
      {tab === 'general' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)', maxWidth: 600 }}>
          <h3 style={{ fontWeight: 600, marginBottom: 16 }}>General Settings</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Currency</label>
              <input style={inputStyle} value={settings.currency || ''} onChange={e => setSettings({ ...settings, currency: e.target.value })} placeholder="USD" /></div>
            <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Stale Lead Days</label>
              <input style={inputStyle} type="number" value={settings.stale_lead_days || ''} onChange={e => setSettings({ ...settings, stale_lead_days: e.target.value })} placeholder="30" /></div>
            <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Lead Code Prefix</label>
              <input style={inputStyle} value={settings.lead_code_prefix || ''} onChange={e => setSettings({ ...settings, lead_code_prefix: e.target.value })} placeholder="LD-" /></div>
            <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Proposal Number Format</label>
              <input style={inputStyle} value={settings.proposal_number_format || ''} onChange={e => setSettings({ ...settings, proposal_number_format: e.target.value })} placeholder="PROP-{YYYY}-{####}" /></div>
            <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Auto-Assign Leads</label>
              <select style={inputStyle} value={settings.auto_assign || 'false'} onChange={e => setSettings({ ...settings, auto_assign: e.target.value })}>
                <option value="false">Disabled</option><option value="true">Enabled</option>
              </select></div>
          </div>
          <button onClick={saveSettings} disabled={saving} style={{ marginTop: 20, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Pipeline Stages */}
      {tab === 'stages' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Pipeline Stages</h3>
            <button onClick={() => { setShowAddModal('stage'); setAddForm({ name: '', probability: 0, color: '#6366f1', sort_order: stages.length + 1 }); }}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>+ Add Stage</button>
          </div>
          {renderList(stages, 'stage')}
        </div>
      )}

      {/* Activity Types */}
      {tab === 'activities' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Activity Types</h3>
            <button onClick={() => { setShowAddModal('activity'); setAddForm({ name: '', icon: '📞', color: '#3b82f6' }); }}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>+ Add Type</button>
          </div>
          {renderList(activityTypes, 'activity')}
        </div>
      )}

      {/* Won/Lost Reasons */}
      {tab === 'wonlost' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 600, color: '#22c55e' }}>Won Reasons</h3>
              <button onClick={() => { setShowAddModal('won'); setAddForm({ reason: '', description: '' }); }}
                style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>+ Add</button>
            </div>
            {wonReasons.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                <div><span style={{ fontWeight: 600 }}>{r.reason}</span>{r.description && <div style={{ fontSize: 12, color: '#9ca3af' }}>{r.description}</div>}</div>
                <button onClick={() => deleteItem('won', r.id)} style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #ef4444', color: '#ef4444', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>×</button>
              </div>
            ))}
            {wonReasons.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No won reasons</div>}
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 600, color: '#ef4444' }}>Lost Reasons</h3>
              <button onClick={() => { setShowAddModal('lost'); setAddForm({ reason: '', description: '' }); }}
                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>+ Add</button>
            </div>
            {lostReasons.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                <div><span style={{ fontWeight: 600 }}>{r.reason}</span>{r.description && <div style={{ fontSize: 12, color: '#9ca3af' }}>{r.description}</div>}</div>
                <button onClick={() => deleteItem('lost', r.id)} style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #ef4444', color: '#ef4444', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>×</button>
              </div>
            ))}
            {lostReasons.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No lost reasons</div>}
          </div>
        </div>
      )}

      {/* Custom Fields */}
      {tab === 'fields' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Custom Fields</h3>
            <button onClick={() => { setShowAddModal('field'); setAddForm({ field_name: '', field_label: '', data_type: 'text', is_required: false, sort_order: customFields.length + 1 }); }}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>+ Add Field</button>
          </div>
          {renderList(customFields, 'field', 'field_label')}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440 }}>
            <h3 style={{ marginBottom: 20 }}>Add {showAddModal === 'stage' ? 'Pipeline Stage' : showAddModal === 'activity' ? 'Activity Type' : showAddModal === 'won' ? 'Won Reason' : showAddModal === 'lost' ? 'Lost Reason' : 'Custom Field'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(showAddModal === 'stage') && <>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label><input style={inputStyle} value={addForm.name || ''} onChange={e => setAddForm({ ...addForm, name: e.target.value })} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Probability %</label><input style={inputStyle} type="number" value={addForm.probability || 0} onChange={e => setAddForm({ ...addForm, probability: e.target.value })} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Color</label><input style={inputStyle} type="color" value={addForm.color || '#6366f1'} onChange={e => setAddForm({ ...addForm, color: e.target.value })} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Sort Order</label><input style={inputStyle} type="number" value={addForm.sort_order || 1} onChange={e => setAddForm({ ...addForm, sort_order: e.target.value })} /></div>
              </>}
              {(showAddModal === 'activity') && <>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label><input style={inputStyle} value={addForm.name || ''} onChange={e => setAddForm({ ...addForm, name: e.target.value })} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Icon (emoji)</label><input style={inputStyle} value={addForm.icon || ''} onChange={e => setAddForm({ ...addForm, icon: e.target.value })} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Color</label><input style={inputStyle} type="color" value={addForm.color || '#3b82f6'} onChange={e => setAddForm({ ...addForm, color: e.target.value })} /></div>
              </>}
              {(showAddModal === 'won' || showAddModal === 'lost') && <>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Reason *</label><input style={inputStyle} value={addForm.reason || ''} onChange={e => setAddForm({ ...addForm, reason: e.target.value })} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label><textarea style={{ ...inputStyle, minHeight: 50 }} value={addForm.description || ''} onChange={e => setAddForm({ ...addForm, description: e.target.value })} /></div>
              </>}
              {(showAddModal === 'field') && <>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Field Name *</label><input style={inputStyle} value={addForm.field_name || ''} onChange={e => setAddForm({ ...addForm, field_name: e.target.value })} placeholder="field_name_snake" /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Field Label *</label><input style={inputStyle} value={addForm.field_label || ''} onChange={e => setAddForm({ ...addForm, field_label: e.target.value })} placeholder="Display Label" /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Data Type</label>
                  <select style={inputStyle} value={addForm.data_type || 'text'} onChange={e => setAddForm({ ...addForm, data_type: e.target.value })}>
                    {['text', 'textarea', 'number', 'decimal', 'date', 'datetime', 'boolean', 'select', 'multiselect', 'radio', 'checkbox', 'email', 'phone', 'url', 'currency', 'percentage', 'file', 'image'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select></div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={addForm.is_required || false} onChange={e => setAddForm({ ...addForm, is_required: e.target.checked })} />
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Required</label>
                </div>
              </>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowAddModal(null); setAddForm({}); }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={addItem} style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
